import React, { useState } from 'react';
import { API } from '../../api/client';
import { ApplicationDetail, ScoringGroup, ScoringRow } from '../evaluatorData';

const PITCH_TAB = '__pitch__';

/** Read-only "Application Details" panel shared by the live scorer and the
 *  past-evaluations archive. Schema-driven: tabs come from the application's own
 *  sections, rows render by their `hint` (chips / cert / esg / plain text), and a
 *  synthetic "Pitch Materials" tab aggregates every uploaded file across sections.
 *  `appId` scopes the presigned pitch-material URLs. */
export default function ApplicationDetailPanel({ appId, detail }: { appId: string; detail: ApplicationDetail }) {
  const sections = detail.sections ?? [];
  const hasPitch = (detail.pitchMaterials?.length ?? 0) > 0;
  const [tab, setTab] = useState<string>(sections[0]?.id ?? PITCH_TAB);

  // Fetch a short-lived presigned URL for a pitch-material file and open it in a
  // new tab (PDFs/videos preview inline; the viewer can download from there).
  const openPitchMaterial = async (key: string) => {
    try {
      const res = await API.fiFileUrl(appId, key);
      if (res?.url) window.open(res.url, '_blank', 'noopener');
    } catch {
      /* file may be unavailable (uploads not configured / access denied) — no-op */
    }
  };

  const activeSection = sections.find(s => s.id === tab);

  return (
    <div className="detail-panel detail-panel-card">
      <div className="detail-head">
        <div className="detail-head-title">
          Application Details <span>(read-only)</span>
        </div>
        <div className="detail-tabs">
          {sections.map(s => (
            <div key={s.id} className={`detail-tab${tab === s.id ? ' active' : ''}`} onClick={() => setTab(s.id)}>
              {s.shortLabel || s.title}
            </div>
          ))}
          {hasPitch && (
            <div className={`detail-tab${tab === PITCH_TAB ? ' active' : ''}`} onClick={() => setTab(PITCH_TAB)}>
              Pitch Materials
            </div>
          )}
        </div>
      </div>

      <div className="detail-body">
        {activeSection && activeSection.groups.map((g, i) => <Group key={i} group={g} />)}

        {tab === PITCH_TAB && (
          <div style={{ color: '#888', fontSize: 13 }}>
            <div className="detail-group-title">Pitch Materials</div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {detail.pitchMaterials!.map(m => (
                <button
                  key={m.key}
                  type="button"
                  className="detail-file-chip"
                  title={m.label}
                  onClick={() => openPitchMaterial(m.key)}
                >
                  {m.contentType.startsWith('video') ? '▶' : '📎'} {m.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** One sub-group: an optional heading followed by its rows. */
function Group({ group }: { group: ScoringGroup }) {
  return (
    <div style={{ marginBottom: 14 }}>
      {group.subgroup && <div className="detail-group-title">{group.subgroup}</div>}
      {group.rows.map((r, i) => <Row key={r.questionId ?? `r-${i}`} row={r} />)}
    </div>
  );
}

/** A single answer row, rendered by its display hint. */
function Row({ row }: { row: ScoringRow }) {
  if (row.hint === 'cert') {
    return (
      <div className="detail-row" style={{ marginBottom: 6 }}>
        <span className={row.ok ? 'adet-cert-ok' : 'adet-cert-no'}>{row.ok ? '✓' : '✗'} {row.label}</span>
      </div>
    );
  }

  if (row.hint === 'chips') {
    const items = row.items ?? [];
    return (
      <div className="detail-row" style={{ marginBottom: 8 }}>
        <div className="k">{row.label}</div>
        {items.length ? (
          <div className="adet-chips" style={{ marginTop: 4 }}>
            {items.map(x => <span key={x} className="adet-chip">{x}</span>)}
          </div>
        ) : (
          <div className="v">—</div>
        )}
      </div>
    );
  }

  if (row.hint === 'esg') {
    const items = row.items ?? [];
    return (
      <div className="detail-row" style={{ marginBottom: 8 }}>
        <div className="k">{row.label}</div>
        {items.length ? (
          items.map(x => (
            <div key={x} className="adet-esg-row"><span className="yes">✓</span>{x}</div>
          ))
        ) : (
          <div className="v">—</div>
        )}
      </div>
    );
  }

  if (row.hint === 'link') {
    const url = (row.value || '').trim();
    const href = url && !/^https?:\/\//i.test(url) ? `https://${url}` : url;
    return (
      <div className="detail-row">
        <div className="k">{row.label}</div>
        <div className="v">
          {href
            ? <a className="adet-link" href={href} target="_blank" rel="noopener noreferrer">{url}</a>
            : '—'}
        </div>
      </div>
    );
  }

  // Default: label / value.
  return (
    <div className="detail-row">
      <div className="k">{row.label}</div>
      <div className="v">{row.value || '—'}</div>
    </div>
  );
}
